<template>
  <div class="privacy-policy-page">
    <div class="privacy-policy-container">
      <div class="section-header">
        <h1 class="section-title">{{ t('privacyPolicy.title') }}</h1>
      </div>

      <div class="policy-content">
        <UISafeHtml tag="p" :content="policyContent?.intro" />

        <h2>1. {{ policyContent?.sections?.general?.title }}</h2>
        <UISafeHtml tag="p" :content="policyContent?.sections?.general?.content" />

        <h2>2. {{ policyContent?.sections?.obligations?.title }}</h2>
        <UISafeHtml tag="p" :content="policyContent?.sections?.obligations?.content" />

        <h2>3. {{ policyContent?.sections?.usage?.title }}</h2>
        <UISafeHtml tag="p" :content="policyContent?.sections?.usage?.content" />
        <ul class="policy-list">
          <UISafeHtml
            v-for="item in policyUsageList"
            :key="item"
            tag="li"
            :content="item"
          />
        </ul>

        <h2>4. {{ policyContent?.sections?.retention?.title }}</h2>
        <UISafeHtml tag="p" :content="policyContent?.sections?.retention?.content" />

        <h2>5. {{ policyContent?.sections?.cookies?.title }}</h2>
        <UISafeHtml tag="p" :content="policyContent?.sections?.cookies?.content" />

        <h2>6. {{ policyContent?.sections?.hosting?.title }}</h2>
        <UISafeHtml tag="p" :content="policyContent?.sections?.hosting?.content" />

        <h2>7. {{ policyContent?.sections?.rights?.title }}</h2>
        <UISafeHtml tag="p" :content="policyContent?.sections?.rights?.content" />
        <ul class="policy-list">
          <li v-for="right in policyRightsList" :key="right.title">
            <strong>{{ right.title }}:</strong> {{ right.description }}
          </li>
        </ul>

        <h2>8. {{ policyContent?.sections?.contact?.title }}</h2>
        <UISafeHtml tag="p" :content="policyContent?.sections?.contact?.content" />

        <h2>9. {{ policyContent?.sections?.consent?.title }}</h2>
        <UISafeHtml tag="p" :content="policyContent?.sections?.consent?.content" />
      </div>
      
      <div class="back-button-wrap">
        <UIButton
          variant="secondary-arrow"
          arrow-direction="left"
          :href="`/${route.params.locale}`"
          :outline="false"
          :text="policyContent?.backButton ?? ''"
          padding="18px 34px"
          rounded="8px"
          size="large"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useLocaleSwitch } from '@/composables/locale'
import { useSeo } from '@/composables/seo'

interface PolicySection {
  title: string
  content: string
  list?: string[] | { title: string; description: string }[]
}

interface PolicyContent {
  intro?: string
  backButton?: string
  sections?: {
    general?: PolicySection
    obligations?: PolicySection
    usage?: PolicySection
    retention?: PolicySection
    cookies?: PolicySection
    hosting?: PolicySection
    rights?: PolicySection
    contact?: PolicySection
    consent?: PolicySection
  }
}

definePageMeta({
  layout: 'full-width',
})

const { t, tm } = useI18n()
const route = useRoute()
useLocaleSwitch()

// Контент из i18n (privacyPolicy в en/uk/ru.json)
const policyContent = computed<PolicyContent>(() => (tm('privacyPolicy') as PolicyContent) ?? {})

const policyUsageList = computed(() => {
  const list = policyContent.value?.sections?.usage?.list ?? []
  return Array.isArray(list) ? list.filter((item): item is string => typeof item === 'string') : []
})

const policyRightsList = computed(() => {
  const list = policyContent.value?.sections?.rights?.list ?? []
  return Array.isArray(list) ? list.filter((r): r is { title: string; description: string } => typeof r === 'object' && r !== null && 'title' in r) : []
})

// Настройка SEO из i18n (privacyPolicy.seo в en/uk/ru.json)
const { setupArticleSeo } = useSeo()
const seoData = tm('privacyPolicy.seo') as { title: Record<string, string>; description: Record<string, string>; slug: Record<string, string> } | undefined

if (seoData?.title && seoData?.description && seoData?.slug) {
  setupArticleSeo({
    title: seoData.title,
    description: seoData.description,
    slug: seoData.slug,
    author: 'Bible School',
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0]
  })
}

</script>

<style scoped>
.privacy-policy-page {
  min-height: 100vh;
  padding: 80px 0 100px;
  max-width: var(--max-width-container);
  margin: 0 auto;
  background: #fff;
}

.privacy-policy-container {
  max-width: var(--max-width-container);
  margin: 0 auto;
  padding: 0 40px;
}

.section-header {
  text-align: center;
  margin-bottom: 48px;
}

.section-header .section-title {
  font-family: var(--font-inter);
  font-weight: 700;
  font-size: 48px;
  color: var(--color-section-title);
  margin: 0 auto 16px;
  line-height: 1.2;
}

.policy-content {
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
}

.policy-content :deep(h2) {
  font-family: var(--font-inter);
  font-weight: var(--font-weight-semibold);
  font-size: 24px;
  color: var(--color-dark-blue);
  margin: 2rem 0 1rem 0;
}

.policy-content :deep(p) {
  font-family: var(--font-inter);
  font-size: var(--font-size-body);
  color: var(--color-grey);
  margin-bottom: 1rem;
}

.policy-list {
  margin: 1rem 0;
  padding-left: 1.5rem;
  font-family: var(--font-inter);
  font-size: var(--font-size-body);
  color: var(--color-grey);
}

.policy-list li {
  margin-bottom: 0.5rem;
  line-height: 1.6;
}

.policy-content :deep(.policy-email-wrap) {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.policy-content :deep(.policy-email-icon) {
  flex-shrink: 0;
  vertical-align: middle;
  opacity: 0.9;
}

.policy-content :deep(.policy-link) {
  color: var(--color-teal);
  text-decoration: none;
  transition: color 0.2s ease;
}

.policy-content :deep(.policy-link:hover) {
  color: var(--color-dark-blue);
  text-decoration: underline;
}

.back-button-wrap {
  margin-top: 2rem;
  display: flex;
  justify-content: center;
}

@media (max-width: 1400px) {
  .privacy-policy-container {
    padding: 0 60px;
  }
}

@media (max-width: 991px) {
  .privacy-policy-page {
    padding: 60px 0 80px;
  }

  .privacy-policy-container {
    padding: 0 30px;
  }

  .section-header .section-title {
    font-size: 36px;
  }

  .policy-content :deep(h2) {
    font-size: 20px;
  }
}

@media (max-width: 767px) {
  .privacy-policy-container {
    padding: 0 20px;
  }

  .section-header .section-title {
    font-size: 32px;
  }

  .section-header {
    margin-bottom: 40px;
  }
}

@media (max-width: 575px) {
  .privacy-policy-page {
    padding: 40px 0 60px;
  }

  .section-header .section-title {
    font-size: 28px;
  }
  .section-title{
    margin-bottom: 25px;
  }
}
</style>